import React, { Fragment } from 'react';
import styles from './StudyTeamMemberForm.scss';
import lodash from 'lodash';

import Select from 'react-select';
import Fuse from 'fuse.js/dist/fuse';
import InputField from 'components/common/Study/InputField';
import {
    customStyles, renderOptions, formatGroupLabel,
} from 'components/common/Dropdown/DropDownListHelper';
import { fuseOptions } from 'common/util/fuse/fuse';
import { Conditional } from 'common/components/layout';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { UserCanManageStudyAdmins } from 'common/permissions/components/actionButton';

export const STUDY_TEAM_CAPITAL = {
    ADMIN: 'Study Admin',
    TEAM: 'Study Team Member',
    VIEWER: 'Study Viewer',
    MONITOR: 'Study Monitor',
};

export const STUDY_TEAM_CAPITAL_v2 = {
    ADMIN: 'Admin',
    TEAM: 'Team Member',
    VIEWER: 'Viewer',
    MONITOR: 'Monitor',
};

export const STUDY_TEAM_DESC = {
    ADMIN: 'study admin',
    TEAM: 'study team member',
    VIEWER: 'study viewer',
};

export const STUDY_USER_TYPE = {
    ADMIN: 'admin',
    TEAM: 'team',
    VIEWER: 'viewer',
    MONITOR: 'monitor'
};

export default function StudyTeamMemberForm({
    editStatus,
    form,
    setForm,
    potentialMatches,
    setPotentialMatches,
    teamMembersOptions,
    studyRolesOptions,
    staffDescriptorOptions,
    studyMemberType,
}) {
    const getTeamMemberOption = (id) => teamMembersOptions.find(teamMember => teamMember.value === id);
    const getStudyRoleOption = (id) => studyRolesOptions.find(role => role.value === id);
    const getStaffDescriptorOption = (value) => staffDescriptorOptions.find(staffDescriptor => staffDescriptor.value === value);

    function getPotentialMatch(user, studyMemberType) {
        if (studyMemberType !== STUDY_USER_TYPE.TEAM) {
            return;
        }
        if (lodash.isNil(user)) {
            return setForm(draft => {
                draft.nameOfResearcher = '';
            });
        } else {
            const fusedTeamMemberCollection = new Fuse(staffDescriptorOptions, fuseOptions(['label']));
            const potentialMatches = fusedTeamMemberCollection.search(user.label);
            setPotentialMatches(potentialMatches);
            setForm(draft => {
                draft.nameOfResearcher = potentialMatches.length === 1 ? potentialMatches[0].item.value : '';
            });
        }
    }

    function renderStudyAdminSection(form, setForm) {
        return (
            <React.Fragment data-cy="StudyTeamMemberForm">
                <StudyAdminCheckboxMui form={form} setForm={setForm} />
                <StudyAdminInfo />
            </React.Fragment>
        );
    }

    const SecuredStudyAdminSection = UserCanManageStudyAdmins(() => renderStudyAdminSection(form, setForm), () => { return null; });
    const isStudyTeamMember = studyMemberType === STUDY_USER_TYPE.TEAM;
    const isStudyViewerAndHasRole = studyMemberType === STUDY_USER_TYPE.VIEWER && form.studyRoleId;
    const isStudyAdminAndHasRole = studyMemberType === STUDY_USER_TYPE.ADMIN && form.studyRoleId;
    return (
        studyMemberType !== '' &&
        <Fragment data-cy="StudyTeamMemberForm-Fragment">
            <InputField
                required
                label='Select User'
                htmlFor='user'
            >
                <Select
                    id='user'
                    name='user'
                    isDisabled={editStatus}
                    isClearable={true}
                    options={teamMembersOptions}
                    defaultValue={getTeamMemberOption(form.userId)}
                    value={getTeamMemberOption(form.userId)}
                    placeholder={editStatus ? form.displayName : 'Select User'}
                    styles={customStyles}
                    onChange={user => {
                        getPotentialMatch(user, studyMemberType);
                        setForm(draft => {
                            draft.userId = !lodash.isNil(user) ? user.value : '';
                            draft.displayName = !lodash.isNil(user) ? user.label : '';
                            draft.sub = !lodash.isNil(user) ? user.sub : '';
                        });
                    }}
                    data-cy="StudyTeamMemberForm-user"
                />
            </InputField>
            {
                <Fragment>
                    <InputField
                        required={studyMemberType === STUDY_USER_TYPE.TEAM}
                        label='Assign Role'
                        htmlFor='role'
                        helperText={'A role does not need to be selected for Admins or Viewers. This will prevent this user from being added to the DOA.'}
                    >
                        <Select
                            id='role'
                            name='role'
                            isClearable={true}
                            options={studyRolesOptions}
                            defaultValue={getStudyRoleOption(form.studyRoleId)}
                            value={getStudyRoleOption(form.studyRoleId)}
                            placeholder='Assign Role'
                            styles={customStyles}
                            onChange={role =>
                                setForm(draft => {
                                    draft.studyRoleId = !lodash.isNil(role) ? role.value : '';
                                    draft.studyRole.name = !lodash.isNil(role) ? role.label : '';
                                    draft.studyRole.id = !lodash.isNil(role) ? role.value : '';
                                })
                            }
                            data-cy="StudyTeamMemberForm-role"
                        />
                    </InputField>
                    <InputField
                        required={isStudyTeamMember || isStudyViewerAndHasRole || isStudyAdminAndHasRole}
                        label='Select Data Source'
                        htmlFor='documentFlow'
                        helperText={'Selecting a data source ensures that this member\'s documents will flow into this study'}
                    >
                        <Select
                            id='documentFlow'
                            name='documentFlow'
                            isClearable={true}
                            formatGroupLabel={formatGroupLabel}
                            options={renderOptions(potentialMatches, staffDescriptorOptions)}
                            defaultValue={getStaffDescriptorOption(form.nameOfResearcher)}
                            value={getStaffDescriptorOption(form.nameOfResearcher)}
                            placeholder='Select Data Source'
                            styles={customStyles}
                            onChange={documentAlias =>
                                setForm(draft => {
                                    draft.nameOfResearcher = !lodash.isNil(documentAlias) ? documentAlias.value : '';
                                })
                            }
                            data-cy="StudyTeamMemberForm-documentFlow"
                        />
                    </InputField>
                    {!editStatus && <DetailedText
                        title={'Don\'t see an appropriate data source?'}
                        description={'This means the data source for this user hasn\'t been setup. Check with an admin at your institution'}
                    />}

                    {isStudyTeamMember && editStatus && <SecuredStudyAdminSection />}
                </Fragment>
            }
        </Fragment>
    );
}

export const DetailedText = ({ title, description }) => (
    <div className={styles.details_text} data-cy="StudyTeamMemberForm-DetailedText">
        <h3 data-cy="StudyTeamMemberForm-title">{title}</h3>
        <p data-cy="StudyTeamMemberForm-description">{description}</p>
    </div>
);

export const StudyTeamMemberTypePanel = ({ studyMemberType, setType, label, displayStudyAdminOption }) => {
    return (
        <InputField
            required
            label={label}
            htmlFor='type'
            data-cy="StudyTeamMemberForm-InputField"
        >
            <Conditional condition={displayStudyAdminOption}>
                <TeamRadio
                    team_type={STUDY_USER_TYPE.ADMIN}
                    studyMemberType={studyMemberType}
                    setType={setType}
                    data-cy="StudyTeamMemberForm-TeamRadio" />
            </Conditional>
            <TeamRadio
                team_type={STUDY_USER_TYPE.TEAM}
                studyMemberType={studyMemberType}
                setType={setType}
                data-cy="StudyTeamMemberForm-TeamRadios" />
            <TeamRadio
                team_type={STUDY_USER_TYPE.VIEWER}
                studyMemberType={studyMemberType}
                setType={setType}
                data-cy="StudyTeamMemberForm-TeamRadiod" />
        </InputField>
    );
};

export const TeamRadio = ({ studyMemberType, setType, team_type }) => {
    return (
        <div className={styles.radioContainer} data-cy="StudyTeamMemberForm-TeamRadiom">
            <label>
                <input
                    type='radio'
                    value={STUDY_TEAM_DESC[team_type.toUpperCase()]}
                    defaultValue={studyMemberType}
                    checked={studyMemberType === team_type}
                    onChange={() => setType(team_type)}
                    data-cy="StudyTeamMemberForm-setType"
                />
                {STUDY_TEAM_DESC[team_type.toUpperCase()]}
            </label>
        </div>
    );
};

const StudyAdminCheckboxMui = ({ form, setForm }) => {
    return (
        <FormControlLabel
            data-cy="StudyTeamMemberForm-StudyAdminCheckboxMui"
            control={
                <Checkbox
                    color="primary"
                    checked={form.hasAdminRole}
                    onChange={() => {
                        setForm(draft => {
                            draft.hasAdminRole = !form.hasAdminRole;
                        });
                    }}
                />
            }
            label={'Give user Study Admin permissions'}
        />
    );
};

const StudyAdminInfo = () => {
    return (
        <div data-cy="StudyTeamMemberForm-StudyAdminInfo">
            <DetailedText
                data-cy="StudyTeamMemberForm-DetailedText"
                title={'Study Admin'}
                description={'An internal user who has the ability to manage all aspects of the study including Study Details, Study Team, and Expectations. They are a key administrator of the Study.'}
            />

            <DetailedText
                data-cy="StudyTeamMemberForm-DetailedTexts"
                title={'Admin Permissions'}
                description={'Giving admin permissions to Study Team Members will not affect their role on the Study Team / Delegation Log.'}
            />
        </div>
    );
};
